import { useEffect, useState } from 'react';
import api from "./services/api-configuration";

export class AuthService {
    static #employeeId = null;
    static #roleCache = new Map();

    static getEmployeeId() {
        if (!this.#employeeId) {
            const encoded = localStorage.getItem("employee_id");
            if (!encoded) return null;

            try {
                this.#employeeId = parseInt(Buffer.from(encoded, 'base64').toString('ascii'));
            } catch (error) {
                console.error('Error decoding employee ID:', error);
                return null;
            }
        }
        return this.#employeeId;
    }

    static async checkRoles(roleGroupId) {
        if (!roleGroupId || !this.getEmployeeId()) {
            return [];
        }

        // Check cache first
        const cacheKey = `role_group_${roleGroupId}`;
        if (this.#roleCache.has(cacheKey)) {
            return this.#roleCache.get(cacheKey);
        }

        try {
            const response = await api.post("/checkRole", {
                employee_id: this.getEmployeeId(),
                role_group_id: roleGroupId
            });

            const roles = (response.data || [])
                .filter(role => role?.permission === true)
                .map(role => role.role_name);

            // Cache the result
            this.#roleCache.set(cacheKey, roles);
            return roles;

        } catch (error) {
            console.error("Error checking user roles:", error);
            return [];
        }
    }

    static async hasRole(roleGroupId, roleName) {
        const roles = await this.checkRoles(roleGroupId);
        return roles.includes(roleName);
    }

    static async hasAnyRole(roleGroupId, roleNames) {
        const roles = await this.checkRoles(roleGroupId);
        return roleNames.some(role => roles.includes(role));
    }

    static async hasAllRoles(roleGroupId, roleNames) {
        const roles = await this.checkRoles(roleGroupId);
        return roleNames.every(role => roles.includes(role));
    }

    static clearCache() {
        this.#roleCache.clear();
        this.#employeeId = null;
    }
}


// hooks/useAuth.js

export const useAuth = (roleGroupId) => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const userRoles = await AuthService.checkRoles(roleGroupId);
                setRoles(userRoles);
            } catch (error) {
                console.error('Error fetching roles:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRoles();
    }, [roleGroupId]);

    const hasRole = (roleName) => roles.includes(roleName);
    const hasAnyRole = (roleNames) => roleNames.some(role => roles.includes(role));
    const hasAllRoles = (roleNames) => roleNames.every(role => roles.includes(role));

    return {
        roles,
        loading,
        hasRole,
        hasAnyRole,
        hasAllRoles
    };
};